import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CartItem, CartContextType } from '../types/cart';

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem('cartItems');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('cartItems', JSON.stringify(cartItems));
    } catch {}
  }, [cartItems]);

  // Seçenekleri karşılaştırmak için yardımcı fonksiyon
  const isSameOptions = (item1: CartItem, item2: CartItem) => {
    // Eski format kontrolü
    if (item1.options && item2.options) {
      return (
        item1.options.size === item2.options.size &&
        item1.options.strength === item2.options.strength
      );
    }

    // Yeni format kontrolü
    if (item1.dynamicOptions && item2.dynamicOptions) {
      const keys1 = Object.keys(item1.dynamicOptions).sort();
      const keys2 = Object.keys(item2.dynamicOptions).sort();

      if (keys1.length !== keys2.length) {return false}

      return keys1.every(key =>
        item1.dynamicOptions![key] === item2.dynamicOptions![key]
      );
    }

    // Hiç seçenek yoksa
    return !item1.options && !item2.options && !item1.dynamicOptions && !item2.dynamicOptions;
  };

  // Item'ı identifier ile eşleştirmek için yardımcı fonksiyon
  const matchesIdentifier = (item: CartItem, identifier: any) => {
    if (identifier.dynamicOptions) {
      if (!item.dynamicOptions) {return false}

      const keys1 = Object.keys(identifier.dynamicOptions).sort();
      const keys2 = Object.keys(item.dynamicOptions).sort();

      if (keys1.length !== keys2.length) {return false}

      return keys1.every(key =>
        identifier.dynamicOptions[key] === item.dynamicOptions![key]
      );
    } else {
      // Eski format
      return (
        item.options?.size === identifier.size && item.options?.strength === identifier.strength
      );
    }
  };

  const addToCart = (item: Omit<CartItem, 'quantity'>, quantity: number = 1) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(
        i => i.id === item.id && isSameOptions(i, item as CartItem)
      );

      if (existingItem) {
        return prevItems.map(i =>
          i.id === item.id && isSameOptions(i, item as CartItem)
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prevItems, { ...item, quantity }];
    });
  };

  const removeFromCart = (id: number, identifier: any) => {
    setCartItems(prevItems =>
      prevItems.filter(item => !(item.id === id && matchesIdentifier(item, identifier)))
    );
  };

  const updateQuantity = (id: number, identifier: any, quantity: number) => {
    if (quantity < 1) {return;}
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === id && matchesIdentifier(item, identifier) ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
